import React, { createContext, useContext, useState } from "react"
import { Slot } from "@radix-ui/react-slot"
import { PanelLeft } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { ScrollArea } from "./scroll-area"

const mobileSafeAreaTop = {
  paddingTop: 'max(1rem, env(safe-area-inset-top))'
}

const SidebarContext = createContext({})

const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}

const SidebarProvider = ({ children, defaultOpen = true }) => {
  const [isMobile, setIsMobile] = useState(false)

  // Initialize open state based on screen size
  const getInitialOpenState = () => {
    if (typeof window !== 'undefined') {
      const isMobileScreen = window.innerWidth < 768
      return isMobileScreen ? false : defaultOpen
    }
    return defaultOpen
  }

  const [open, setOpen] = useState(getInitialOpenState)

  React.useEffect(() => {
    const checkMobile = () => {
      const isMobileScreen = window.innerWidth < 768
      const wasMobile = isMobile
      setIsMobile(isMobileScreen)

      // Only close sidebar when transitioning FROM desktop TO mobile
      // Don't interfere with manual toggle in mobile
      if (!wasMobile && isMobileScreen && open) {
        setOpen(false)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [isMobile, open])

  return (
    <SidebarContext.Provider value={{ open, setOpen, isMobile }}>
      {children}
      {/* Mobile backdrop/overlay */}
      {isMobile && open && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setOpen(false)}
        />
      )}
    </SidebarContext.Provider>
  )
}

const Sidebar = React.forwardRef(({ className, children, variant = "inset", side = "left", collapsible = "icon", ...props }, ref) => {
  const { open, isMobile } = useSidebar()

  return (
    <div
      ref={ref}
      data-sidebar="sidebar"
      data-variant={variant}
      data-side={side}
      data-state={open ? "expanded" : "collapsed"}
      data-collapsible={collapsible}
      className={cn(
        "group peer relative flex flex-col bg-background border-r h-screen transition-all duration-200 ease-in-out",
        // Sidebar behavior based on collapsible mode
        collapsible === "icon"
          ? isMobile
            ? open
              ? "fixed inset-y-0 left-0 z-50 w-64 translate-x-0 shadow-lg"
              : "fixed inset-y-0 left-0 z-50 w-16 -translate-x-full"
            : open
            ? "w-64"
            : "w-16" // Icon mode: show icons when collapsed
          : // Non-collapsible or other modes
            isMobile
            ? open
              ? "fixed inset-y-0 left-0 z-50 w-64 translate-x-0 shadow-lg"
              : "fixed inset-y-0 left-0 z-50 w-64 -translate-x-full"
            : "w-64",
        className
      )}
      style={isMobile && open ? mobileSafeAreaTop : undefined}
      {...props}
    >
      {children}
    </div>
  )
})
Sidebar.displayName = "Sidebar"

const SidebarHeader = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    >
      {children}
    </div>
  )
})
SidebarHeader.displayName = "SidebarHeader"

const SidebarContent = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <ScrollArea
      ref={ref}
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        "group-data-[state=collapsed]:overflow-hidden",
        className
      )}
      {...props}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        {children}
      </div>
    </ScrollArea>
  )
})
SidebarContent.displayName = "SidebarContent"

const SidebarFooter = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2 mt-auto", className)}
      {...props}
    >
      {children}
    </div>
  )
})
SidebarFooter.displayName = "SidebarFooter"

const SidebarTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const { open, setOpen } = useSidebar()

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      className={cn("h-8 w-8", className)}
      onClick={() => setOpen(!open)}
      {...props}
    >
      <PanelLeft className="h-4 w-4" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
})
SidebarTrigger.displayName = "SidebarTrigger"

const SidebarMenu = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <ul
      ref={ref}
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...props}
    >
      {children}
    </ul>
  )
})
SidebarMenu.displayName = "SidebarMenu"

const SidebarMenuItem = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <li
      ref={ref}
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative min-w-0", className)}
      {...props}
    >
      {children}
    </li>
  )
})
SidebarMenuItem.displayName = "SidebarMenuItem"

const SidebarMenuButton = React.forwardRef(({
  className,
  children,
  Icon,
  isActive = false,
  active,
  badge,
  size = "default",
  tooltip,
  asChild = false,
  ...props
}, ref) => {
  const { open } = useSidebar()
  // Backward compat: aceptar `active` ademas de `isActive`
  const activeState = isActive || active

  const Comp = asChild ? Slot : "button"

  // Si children es string, lo usamos como tooltip si no se paso uno explicito
  const computedTooltip = tooltip || (typeof children === 'string' ? children : undefined)

  return (
    <Comp
      ref={ref}
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={activeState}
      className={cn(
        // Base
        "peer/menu-button flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-colors",
        // States
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        // Active state via data attribute
        "data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground",
        // Icon styling automatica
        "[&>svg]:size-4 [&>svg]:shrink-0",
        // Sizes
        size === "default" && "h-8",
        size === "sm" && "h-7 text-xs",
        size === "lg" && "h-12 text-sm",
        // Collapsed mode
        !open && "justify-center",
        className
      )}
      title={computedTooltip}
      {...props}
    >
      {Icon && <Icon />}
      {open && (
        typeof children === 'string'
          ? <span className="flex-1 truncate min-w-0">{children}</span>
          : children
      )}
      {open && badge && (
        <span className="ml-auto flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
          {badge}
        </span>
      )}
    </Comp>
  )
})
SidebarMenuButton.displayName = "SidebarMenuButton"

const SidebarGroupLabel = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn("text-sidebar-foreground/70 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium group-data-[state=collapsed]:opacity-0 group-data-[state=collapsed]:pointer-events-none", className)}
      {...props}
    >
      {children}
    </div>
  )
})
SidebarGroupLabel.displayName = "SidebarGroupLabel"

const SidebarGroupContent = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm flex flex-col gap-2", className)}
      {...props}
    >
      {children}
    </div>
  )
})
SidebarGroupContent.displayName = "SidebarGroupContent"

// Keep SidebarNav for backward compatibility
const SidebarNav = SidebarMenu

const SidebarNavItem = React.forwardRef(({
  className,
  children,
  Icon,
  active = false,
  badge,
  onClick,
  disabled = false,
  ...props
}, ref) => {
  const { open } = useSidebar()

  return (
    <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className="group/menu-item relative">
      <button
        ref={ref}
        data-slot="sidebar-menu-button"
        data-sidebar="menu-button"
        data-size="default"
        data-active={active}
        className={cn(
          "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left outline-hidden ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 text-sm",
          active && "bg-accent text-accent-foreground font-medium",
          !open && "justify-center",
          className
        )}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        {...props}
      >
        {Icon && (
          <Icon className="h-4 w-4 flex-shrink-0" />
        )}

        {open && (
          <span className="flex-1 truncate text-left">
            {children}
          </span>
        )}

        {open && badge && (
          <span className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
            {badge}
          </span>
        )}
      </button>
    </li>
  )
})
SidebarNavItem.displayName = "SidebarNavItem"

const SidebarGroup = React.forwardRef(({ className, children, title, ...props }, ref) => {
  return (
    <div ref={ref} data-slot="sidebar-group" data-sidebar="group" className={cn("relative flex w-full min-w-0 flex-col px-2 py-0.5", className)} {...props}>
      {title && (
        <SidebarGroupLabel>
          {title}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        {children}
      </SidebarGroupContent>
    </div>
  )
})
SidebarGroup.displayName = "SidebarGroup"

const SidebarMenuSub = React.forwardRef(({ className, ...props }, ref) => {
  const { open } = useSidebar()
  if (!open) return null
  return (
    <ul
      ref={ref}
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "mx-3.5 mt-1 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5",
        className
      )}
      {...props}
    />
  )
})
SidebarMenuSub.displayName = "SidebarMenuSub"

const SidebarMenuSubItem = React.forwardRef(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-slot="sidebar-menu-sub-item"
    data-sidebar="menu-sub-item"
    className={cn("group/menu-sub-item relative min-w-0", className)}
    {...props}
  />
))
SidebarMenuSubItem.displayName = "SidebarMenuSubItem"

const SidebarMenuSubButton = React.forwardRef(
  ({ asChild = false, size = "md", isActive = false, active, className, children, Icon, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const activeState = isActive || active
    const computedTooltip = typeof children === 'string' ? children : undefined

    return (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-sub-button"
        data-sidebar="menu-sub-button"
        data-size={size}
        data-active={activeState}
        className={cn(
          "flex h-7 w-full min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-left text-sidebar-foreground outline-none ring-sidebar-ring transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground",
          "[&>svg]:size-4 [&>svg]:shrink-0",
          size === "sm" && "text-xs",
          size === "md" && "text-sm",
          className
        )}
        title={computedTooltip}
        {...props}
      >
        {Icon && <Icon />}
        {typeof children === 'string'
          ? <span className="flex-1 truncate min-w-0">{children}</span>
          : children}
      </Comp>
    )
  }
)
SidebarMenuSubButton.displayName = "SidebarMenuSubButton"

const SidebarInset = React.forwardRef(({ className, ...props }, ref) => {
  const { isMobile } = useSidebar()

  return (
    <main
      ref={ref}
      className={cn(
        "relative flex min-h-screen flex-1 flex-col bg-background transition-all duration-200 ease-in-out",
        isMobile && "w-full", // Full width on mobile when sidebar is overlay
        className
      )}
      {...props}
    />
  )
})
SidebarInset.displayName = "SidebarInset"

export {
  Sidebar,
  SidebarProvider,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarNav,
  SidebarNavItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarInset,
  useSidebar,
}
